import { Coins } from 'lucide-react'

import { useCredits } from '@/features/billing/useCredits'
import { cn } from '@/lib/utils/cn'

/** Below this, the number stops being ambient and starts being a warning. */
const LOW = 10

/**
 * What the account has left, in the bar, while they work.
 *
 * Deliberately *available* credits rather than the settled balance: a turn in flight is
 * already holding some, and a number that ignores that would say 12 to someone who can
 * spend 5. Hovering explains the difference, which is the only place in the interface where
 * the hold is worth naming.
 *
 * Rendered at all only when the server says billing is on. While it is off there is no
 * balance to speak of, and showing "0 credits" would read as "you are out" to every user of
 * a product that is not charging anybody.
 */
export function CreditsPill({ className }: { className?: string }) {
  const { data, enabled } = useCredits()
  if (!enabled || !data) return null

  const available = data.available_credits
  const low = available <= LOW
  const held = data.reserved_credits

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1',
        'text-xs font-medium tabular-nums',
        low
          ? 'border-waiting/30 bg-waiting-wash text-waiting'
          : 'border-line-strong bg-raised text-ink-dim',
        className,
      )}
      title={
        held > 0
          ? `${available} of ${data.balance_credits} credits available — ${held} held by a request that is still running`
          : `${available} credits available`
      }
      data-testid="credits-pill"
    >
      <Coins aria-hidden="true" className="h-3.5 w-3.5" />
      <span className="sr-only">Credits: </span>
      {available}
      <span className="hidden sm:inline">{available === 1 ? 'credit' : 'credits'}</span>
    </span>
  )
}
